import React, { useState, useEffect, useCallback } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useUser } from "../context/UserContext";
import { ToastProvider, useToast } from "../admin/components/Toast";
import { useConfirm } from "../admin/components/ConfirmDialog";
import AdminSidebar from "../admin/components/AdminSidebar";
import PostJobPage from "../admin/pages/PostJobPage";
import AddPartnerPage from "../admin/pages/AddPartnerPage";
import AddEnrollmentPage from "../admin/pages/AddEnrollmentPage";
import EditEnrollmentPage from "../admin/pages/EditEnrollmentPage";
import TestimonialsPage from "../admin/pages/TestimonialsPage";
import AddTestimonialPage from "../admin/pages/AddTestimonialPage";
import EditTestimonialPage from "../admin/pages/EditTestimonialPage";
import BlogsAdminPage from "../admin/pages/BlogsAdminPage";
import AddBlogPage from "../admin/pages/AddBlogPage";
import EditBlogPage from "../admin/pages/EditBlogPage";
import {
  fetchAdminStats,
  fetchRecentActivity,
  fetchCourses,
  fetchJobs,
  fetchEnrollments,
  fetchApplications,
  fetchCounselRequests,
  fetchCategories,
  fetchProviders,
  createCourse,
  updateCourse,
  deleteCourse,
  deleteJob,
  updateJob,
  updateCounselStatus,
  createCategory,
  deleteCategory,
  updateCategory,
  updateProvider,
  deleteEnrollment,
  updateEnrollmentStatus,
  updateApplicationStatus,
  downloadApplicationsExcel,
  deleteApplication,
  downloadCounselExcel,
  downloadEnrollmentsExcel,
  downloadCoursesExcel,
  fetchTestimonials,
  fetchTestimonialsAdmin,
  createTestimonial,
  updateTestimonial,
  deleteTestimonial,
  fetchAllUsers,
  toggleUserStatus,
  deleteUser,
} from "../services/api";
import Swal from "sweetalert2";

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: "smooth" });
};

// ─── Pagination ───────────────────────────────────────────────────────────────
const Pagination = ({ page, pages, onPage }) => {
  if (pages <= 1) return null;
  const items = [];
  for (let i = 1; i <= pages; i++) {
    if (i === 1 || i === pages || Math.abs(i - page) <= 2) items.push(i);
    else if (items[items.length - 1] !== "...") items.push("...");
  }
  return (
    <div
      style={{
        display: "flex",
        gap: 6,
        alignItems: "center",
        justifyContent: "center",
        marginTop: 16,
        flexWrap: "wrap",
      }}
    >
      <button
        onClick={() => onPage(page - 1)}
        disabled={page <= 1}
        style={{
          padding: "5px 12px",
          borderRadius: 7,
          border: "1px solid var(--border)",
          background: page <= 1 ? "var(--bg2)" : "white",
          cursor: page <= 1 ? "not-allowed" : "pointer",
          fontSize: 13,
        }}
      >
        ← Prev
      </button>
      {items.map((p, i) =>
        p === "..." ? (
          <span
            key={"d" + i}
            style={{ padding: "5px 8px", color: "var(--text3)", fontSize: 13 }}
          >
            …
          </span>
        ) : (
          <button
            key={p}
            onClick={() => onPage(p)}
            style={{
              padding: "5px 12px",
              borderRadius: 7,
              border: "1px solid var(--border)",
              background: p === page ? "var(--accent)" : "white",
              color: p === page ? "white" : "var(--text)",
              cursor: "pointer",
              fontWeight: p === page ? 700 : 400,
              fontSize: 13,
            }}
          >
            {p}
          </button>
        ),
      )}
      <button
        onClick={() => onPage(page + 1)}
        disabled={page >= pages}
        style={{
          padding: "5px 12px",
          borderRadius: 7,
          border: "1px solid var(--border)",
          background: page >= pages ? "var(--bg2)" : "white",
          cursor: page >= pages ? "not-allowed" : "pointer",
          fontSize: 13,
        }}
      >
        Next →
      </button>
    </div>
  );
};

const StatusBadge = ({ active }) => (
  <span className={`admin-badge ${active ? "green" : "orange"}`}>
    {active ? "Active" : "Inactive"}
  </span>
);

// ─── Dashboard ────────────────────────────────────────────────────────────────
const DashboardSection = ({ stats }) => {
  const [activity, setActivity] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(true);

  useEffect(() => {
    fetchRecentActivity()
      .then((r) => setActivity(r.data.data || []))
      .catch(() => setActivity([]))
      .finally(() => setLoadingActivity(false));
  }, []);

  const actionColor = (a) =>
    a === "Enrolled" ? "blue" : a === "Applied for Job" ? "orange" : "green";

  return (
    <div className="admin-section active">
      <div className="admin-header">
        <div className="admin-title">📊 Dashboard Overview</div>
        <span style={{ fontSize: 12, color: "var(--text3)" }}>Live data</span>
      </div>
      <div className="admin-stat-grid">
        {[
          {
            num: stats.enrollments ?? "—",
            label: "Total Enrollments",
            change: "All time",
          },
          {
            num: stats.courses ?? "—",
            label: "Total Courses",
            change: "In database",
          },
          {
            num: stats.jobs ?? "—",
            label: "Active Jobs",
            change: "Live listings",
          },
          {
            num: stats.applications ?? "—",
            label: "CV Applications",
            change: "All time",
          },
          {
            num: stats.counsels ?? "—",
            label: "Counselling Requests",
            change: "All time",
          },
        ].map((s) => (
          <div key={s.label} className="admin-stat-card">
            <div className="admin-stat-num">{s.num}</div>
            <div className="admin-stat-label">{s.label}</div>
            <div className="admin-stat-change">{s.change}</div>
          </div>
        ))}
      </div>
      <div className="admin-card">
        <div
          style={{
            fontFamily: "Syne,sans-serif",
            fontWeight: 700,
            fontSize: 15,
            marginBottom: 14,
          }}
        >
          🔥 Recent Activity
        </div>
        {loadingActivity ? (
          <div
            style={{ textAlign: "center", padding: 24, color: "var(--text3)" }}
          >
            Loading activity…
          </div>
        ) : activity.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: 24,
              color: "var(--text3)",
              fontSize: 13,
            }}
          >
            No recent activity yet.
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Action</th>
                <th>Course/Item</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {activity.map((a, i) => (
                <tr key={i}>
                  <td>{a.user}</td>
                  <td>
                    <span className={`admin-badge ${actionColor(a.action)}`}>
                      {a.action}
                    </span>
                  </td>
                  <td>{a.item}</td>
                  <td style={{ color: "var(--text3)" }}>{a.timeAgo}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ─── Course Form ──────────────────────────────────────────────────────────────
const CourseForm = ({ data, onChange, providers, categories }) => (
  <>
    <div className="admin-form-row">
      <div>
        <label className="admin-form-label">Course Title *</label>
        <input
          className="admin-input"
          value={data.title}
          onChange={(e) => onChange({ ...data, title: e.target.value })}
        />
      </div>
      <div>
        <label className="admin-form-label">Provider</label>
        <select
          className="admin-select"
          value={data.provId}
          onChange={(e) => onChange({ ...data, provId: e.target.value })}
        >
          {providers.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>
    </div>
    <div className="admin-form-row">
      <select
        className="admin-select"
        value={data.catId}
        onChange={(e) => onChange({ ...data, catId: e.target.value })}
      >
        <option value="">-- Select Category --</option> {/* 👈 add karo */}
        {(categories || []).map((c) => (
          <option key={c.id} value={c.id}>
            {c.icon ? `${c.icon} ${c.name}` : c.name}
          </option>
        ))}
      </select>

      <div>
        <label className="admin-form-label">Level</label>
        <select
          className="admin-select"
          value={data.level}
          onChange={(e) => onChange({ ...data, level: e.target.value })}
        >
          {["Beginner", "Intermediate", "Advanced"].map((l) => (
            <option key={l}>{l}</option>
          ))}
        </select>
      </div>
    </div>
    <div className="admin-form-row">
      <div>
        <label className="admin-form-label">Online Price (₹)</label>
        <input
          className="admin-input"
          type="number"
          value={data.price}
          onChange={(e) => onChange({ ...data, price: e.target.value })}
        />
      </div>
      <div>
        <label className="admin-form-label">Online Original Price (₹)</label>
        <input
          className="admin-input"
          type="number"
          value={data.origPrice}
          onChange={(e) => onChange({ ...data, origPrice: e.target.value })}
        />
      </div>

      <div>
        <label className="admin-form-label">Offline Price (₹)</label>
        <input
          className="admin-input"
          type="number"
          value={data.offPrice}
          onChange={(e) => onChange({ ...data, offPrice: e.target.value })}
        />
      </div>
      <div>
        <label className="admin-form-label">Offline Original Price (₹)</label>
        <input
          className="admin-input"
          type="number"
          value={data.offOrigPrice}
          onChange={(e) => onChange({ ...data, offOrigPrice: e.target.value })}
        />
      </div>
    </div>
    <div className="admin-form-row">
      <div>
        <label className="admin-form-label">Duration</label>
        <input
          className="admin-input"
          value={data.duration}
          onChange={(e) => onChange({ ...data, duration: e.target.value })}
        />
      </div>
      <div>
        <label className="admin-form-label">Rating (1–5) ★</label>
        <input
          className="admin-input"
          type="number"
          step="0.1"
          min="1"
          max="5"
          value={data.rating}
          onChange={(e) => onChange({ ...data, rating: e.target.value })}
          placeholder="e.g. 4.5"
        />
      </div>
    </div>
    <div className="admin-form-row">
      <div>
        <label className="admin-form-label">Students Enrolled</label>
        <input
          className="admin-input"
          value={data.students}
          onChange={(e) => onChange({ ...data, students: e.target.value })}
          placeholder="e.g. 12k+"
        />
      </div>
      <div>
        <label className="admin-form-label">Emoji Icon</label>
        <input
          className="admin-input"
          value={data.emoji}
          maxLength={4}
          onChange={(e) => onChange({ ...data, emoji: e.target.value })}
        />
      </div>
    </div>
    <div className="admin-form-row">
      <div>
        <label className="admin-form-label">Certificate?</label>
        <select
          className="admin-select"
          value={String(data.hasCert)}
          onChange={(e) =>
            onChange({ ...data, hasCert: e.target.value === "true" })
          }
        >
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      </div>
      <div>
        <label className="admin-form-label">Exp Level</label>
        <select
          className="admin-select"
          value={data.expLevel}
          onChange={(e) => onChange({ ...data, expLevel: e.target.value })}
        >
          {["any", "fresher", "mid", "senior"].map((l) => (
            <option key={l}>{l}</option>
          ))}
        </select>
      </div>
    </div>
    <div className="admin-form-row">
      <div>
        <label className="admin-form-label">Classes</label>

        <select
          className="admin-select"
          value={data.classes}
          onChange={(e) =>
            onChange({
              ...data,
              classes: e.target.value,
            })
          }
        >
          <option value="WEEKDAYS">Weekdays</option>
          <option value="WEEKEND">Weekend</option>
        </select>
      </div>
    </div>
    <label className="admin-form-label">Tags (comma separated)</label>
    <input
      className="admin-input"
      value={Array.isArray(data.tags) ? data.tags.join(", ") : data.tags}
      onChange={(e) => onChange({ ...data, tags: e.target.value })}
    />

    <label className="admin-form-label">Remarks</label>

    <textarea
      className="admin-input"
      rows={5}
      value={data.remarks || ""}
      onChange={(e) =>
        onChange({
          ...data,
          remarks: e.target.value,
        })
      }
      placeholder="Course remarks / details..."
    />
    <div style={{ display: "flex", gap: 24, marginTop: 12, flexWrap: "wrap" }}>
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 13,
          cursor: "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={!!data.featured}
          onChange={(e) => onChange({ ...data, featured: e.target.checked })}
        />
        Featured
      </label>
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 13,
          cursor: "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={!!data.isImportant}
          onChange={(e) => onChange({ ...data, isImportant: e.target.checked })}
        />
        ⭐ Important (show in homepage Top Courses)
      </label>
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 13,
          cursor: "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={data.isActive !== false}
          onChange={(e) => onChange({ ...data, isActive: e.target.checked })}
        />
        Active (visible in portal)
      </label>
    </div>
  </>
);

// ─── Courses Section ──────────────────────────────────────────────────────────
const CoursesSection = ({ providers, categories }) => {
  const toast = useToast();
  const { confirm, ConfirmComponent } = useConfirm();
  const [courses, setCourses] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showAdd, setShowAdd] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [filterCat, setFilterCat] = useState("");
  const [filterProv, setFilterProv] = useState("");
  const LIMIT = 10;

  const firstValidCat = categories.find(
    (c) => c.name && !c.name.match(/^[0-9a-f]{8}-[0-9a-f]{4}-/),
  );

  const blank = {
    title: "",
    provId: providers[0]?.id || "coursera",
    catId: firstValidCat?.id || categories[0]?.id || "",
    level: "Beginner",
    duration: "",
    price: 0,
    origPrice: 0,
    rating: 4.5,
    students: "0",
    hasCert: true,
    tags: "",
    emoji: "📚",
    featured: false,
    isImportant: false,
    isActive: true,
    expLevel: "any",
    offPrice: 0,
    offOrigPrice: 0,
    // NEW
    remarks: "",
    classes: "WEEKDAYS",
  };
  const [newCourse, setNewCourse] = useState(blank);

  const load = useCallback((p = 1, cat = "", prov = "") => {
    const params = { admin: "true", limit: LIMIT, page: p };
    if (cat) params.catId = cat;
    if (prov) params.provId = prov;
    fetchCourses(params)
      .then((r) => {
        setCourses(r.data.data);
        setPage(r.data.page || p);
        setPages(r.data.pages || 1);
        setTotal(r.data.total || 0);
      })
      .catch(() => {});
  }, []);

  // Initial load
  useEffect(() => {
    load(1, filterCat, filterProv);
    // eslint-disable-next-line
  }, [load]);

  // Re-load when filters change
  useEffect(() => {
    load(1, filterCat, filterProv);
    // eslint-disable-next-line
  }, [filterCat, filterProv]);

  // Mark scrollable wrappers so CSS fade-hint activates
  useEffect(() => {
    const wraps = document.querySelectorAll(".admin-table-wrap");
    wraps.forEach((el) => {
      const check = () => {
        el.classList.toggle("is-scrollable", el.scrollWidth > el.clientWidth);
      };
      check();
      el.addEventListener("scroll", check);
      window.addEventListener("resize", check);
    });
  }, [courses]);

  const handleAdd = async () => {
    if (!newCourse.title.trim()) {
      toast("Course title required", "warning");
      return;
    }
    if (!newCourse.title.trim()) {
      toast("Course title required", "warning");
      return;
    }
    if (!newCourse.catId) {
      // 👈 add karo
      toast("Please select a category", "warning");
      return;
    }
    try {
      const tags =
        typeof newCourse.tags === "string"
          ? newCourse.tags
              .split(",")
              .map((t) => t.trim())
              .filter(Boolean)
          : newCourse.tags;
      await createCourse({ ...newCourse, tags });
      setShowAdd(false);
      setNewCourse(blank);
      load(1);
      toast("Course added! 📚", "success");
    } catch (e) {
      toast(e.response?.data?.message || "Error adding course", "error");
    }
  };

  const handleEditSave = async () => {
    if (!editingCourse.title.trim()) {
      toast("Title required", "warning");
      return;
    }
    try {
      const { id, provider, category, createdAt, updatedAt, ...data } =
        editingCourse;
      if (typeof data.tags === "string")
        data.tags = data.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean);
      await updateCourse(id, data);
      setEditingCourse(null);
      load(page);
      toast("Course updated! ✅", "success");
    } catch (e) {
      toast(e.response?.data?.message || "Error", "error");
    }
  };

  const handleToggleActive = async (c) => {
    try {
      await updateCourse(c.id, { isActive: !c.isActive });
      load(page, filterCat, filterProv);
      toast(
        c.isActive ? "Course hidden from portal" : "Course is now live!",
        "success",
      );
    } catch {
      toast("Error", "error");
    }
  };

  const handleToggleImportant = async (c) => {
    try {
      await updateCourse(c.id, { isImportant: !c.isImportant });
      load(page, filterCat, filterProv);
      toast(
        c.isImportant
          ? "Removed from homepage"
          : "⭐ Added to homepage Top Courses!",
        "success",
      );
    } catch {
      toast("Error", "error");
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm("Delete this course? Cannot be undone.");
    if (!ok) return;
    try {
      await deleteCourse(id);
      load(page, filterCat, filterProv);
      toast("Course deleted", "info");
    } catch (e) {
      toast(e.response?.data?.message || "Error", "error");
    }
  };

  const handleDownloadCoursesExcel = async () => {
    const result = await Swal.fire({
      title: "Download Excel?",
      text: "Are you sure you want to download all courses data?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Download",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;
    try {
      const res = await downloadCoursesExcel();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "courses.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast("Excel downloaded!", "success");
    } catch {
      toast("Download failed", "error");
    }
  };

  return (
    <div className="admin-section active">
      {ConfirmComponent}

      {/* ── Header ── */}
      <div className="admin-header">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            onClick={handleDownloadCoursesExcel}
            style={{
              background: "linear-gradient(135deg,#0ea5e9,#38bdf8)",
              color: "#fff",
              border: "none",
              padding: "8px 14px",
              borderRadius: 8,
              fontSize: 13,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
              boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
              whiteSpace: "nowrap",
            }}
          >
            ⬇ Download Excel
          </button>
          <div className="admin-title">
            📚 Manage Courses{" "}
            <span
              style={{ fontSize: 13, fontWeight: 400, color: "var(--text3)" }}
            >
              ({total} total)
            </span>
          </div>
        </div>
        <button
          className="btn-primary btn-sm"
          onClick={() => {
            setShowAdd((s) => !s);
            setEditingCourse(null);
          }}
        >
          + Add New Course
        </button>
      </div>

      {/* ── Filters ── */}
      <div
        style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}
      >
        <select
          className="admin-select"
          style={{ minWidth: 180, margin: 0 }}
          value={filterCat}
          onChange={(e) => {
            setFilterCat(e.target.value);
            setPage(1);
          }}
        >
          <option value="">🗂 All Categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.icon} {c.name}
            </option>
          ))}
        </select>
        <select
          className="admin-select"
          style={{ minWidth: 180, margin: 0 }}
          value={filterProv}
          onChange={(e) => {
            setFilterProv(e.target.value);
            setPage(1);
          }}
        >
          <option value="">🏢 All Providers</option>
          {providers.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        {(filterCat || filterProv) && (
          <button
            className="btn-secondary btn-sm"
            onClick={() => {
              setFilterCat("");
              setFilterProv("");
            }}
            style={{ fontSize: 12 }}
          >
            ✕ Clear Filters
          </button>
        )}
      </div>

      {/* ── Add form ── */}
      {showAdd && !editingCourse && (
        <div className="admin-card padded" style={{ marginBottom: 20 }}>
          <div
            style={{
              fontFamily: "Syne,sans-serif",
              fontWeight: 700,
              fontSize: 15,
              marginBottom: 16,
            }}
          >
            ➕ Add New Course
          </div>
          <CourseForm
            data={newCourse}
            onChange={setNewCourse}
            providers={providers}
            categories={categories}
          />
          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            <button className="btn-primary btn-sm" onClick={handleAdd}>
              Save Course
            </button>
            <button
              className="btn-secondary btn-sm"
              onClick={() => setShowAdd(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Edit form ── */}
      {editingCourse && (
        <div
          className="admin-card padded"
          style={{ marginBottom: 20, borderLeft: "3px solid var(--accent)" }}
        >
          <div
            style={{
              fontFamily: "Syne,sans-serif",
              fontWeight: 700,
              fontSize: 15,
              marginBottom: 16,
            }}
          >
            ✏️ Edit: {editingCourse.title}
          </div>
          <CourseForm
            data={editingCourse}
            onChange={setEditingCourse}
            providers={providers}
            categories={categories}
          />
          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            <button className="btn-primary btn-sm" onClick={handleEditSave}>
              Update Course
            </button>
            <button
              className="btn-secondary btn-sm"
              onClick={() => setEditingCourse(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Courses Table — scrollable wrapper ── */}
      <div className="admin-card" style={{ position: "relative" }}>
        {/* Scroll hint label */}
        <div
          style={{
            padding: "10px 16px 6px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid var(--border)",
          }}
        >
          <span style={{ fontSize: 12, color: "var(--text3)" }}>
            {courses.length} course{courses.length !== 1 ? "s" : ""} on this
            page
          </span>
          <span
            style={{
              fontSize: 10,
              color: "var(--text3)",
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            ← scroll →
          </span>
        </div>

        {/* THE KEY FIX: admin-table-wrap enables horizontal scroll */}
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Course</th>
                <th>Provider</th>
                <th>Category</th>
                <th>Level</th>
                <th>Duration</th>
                <th>Rating</th>
                <th>Enrolled</th>
                <th>Price</th>
                <th>Orig Price</th>
                <th>Off Price</th>
                <th>Off Orig</th>
                <th>⭐ Imp</th>
                <th>Classes</th>
                <th>Remarks</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {courses.map((c) => (
                <tr key={c.id} style={{ opacity: c.isActive ? 1 : 0.5 }}>
                  <td style={{ minWidth: 180 }}>
                    <strong>
                      {c.emoji} {c.title}
                    </strong>
                    <div style={{ fontSize: 11, color: "var(--text3)" }}>
                      {c.category?.name} · {c.level}
                    </div>
                  </td>
                  <td style={{ minWidth: 100 }}>
                    {c.provider?.name || c.provId}
                  </td>
                  <td style={{ minWidth: 100 }}>{c?.category?.name}</td>
                  <td style={{ minWidth: 80 }}>{c?.level}</td>
                  <td style={{ minWidth: 80 }}>{c?.duration}</td>
                  <td style={{ minWidth: 70 }}>⭐ {c?.rating}</td>
                  <td style={{ minWidth: 80 }}>{c?.students}</td>

                  <td style={{ minWidth: 80 }}>
                    {c.price === 0 ? (
                      <span
                        style={{ color: "var(--accent3)", fontWeight: 600 }}
                      >
                        Free
                      </span>
                    ) : c.price === 1 ? (
                      <span
                        style={{ color: "var(--accent3)", fontWeight: 600 }}
                      >
                        N/A
                      </span>
                    ) : (
                      `₹${c.price?.toLocaleString()}`
                    )}
                  </td>

                  <td style={{ minWidth: 90 }}>
                    {c.origPrice === 0 ? (
                      <span style={{ color: "var(--text3)" }}>Free</span>
                    ) : c.origPrice === 1 ? (
                      <span
                        style={{ color: "var(--accent3)", fontWeight: 600 }}
                      >
                        N/A
                      </span>
                    ) : (
                      `₹${c.origPrice?.toLocaleString()}`
                    )}
                  </td>

                  <td style={{ minWidth: 90 }}>
                    {c.offPrice === 0 ? (
                      <span style={{ color: "var(--text3)" }}>Free</span>
                    ) : c.offPrice === 1 ? (
                      <span
                        style={{ color: "var(--accent3)", fontWeight: 600 }}
                      >
                        N/A
                      </span>
                    ) : (
                      `₹${c.offPrice?.toLocaleString()}`
                    )}
                  </td>

                  <td style={{ minWidth: 90 }}>
                    {c.offOrigPrice === 0 ? (
                      <span style={{ color: "var(--text3)" }}>Free</span>
                    ) : c.offOrigPrice === 1 ? (
                      <span
                        style={{ color: "var(--accent3)", fontWeight: 600 }}
                      >
                        N/A
                      </span>
                    ) : (
                      `₹${c.offOrigPrice?.toLocaleString()}`
                    )}
                  </td>

                  <td style={{ minWidth: 60, textAlign: "center" }}>
                    <button
                      onClick={() => handleToggleImportant(c)}
                      title={
                        c.isImportant
                          ? "Remove from homepage"
                          : "Add to homepage"
                      }
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontSize: 18,
                        opacity: c.isImportant ? 1 : 0.22,
                        transition: "opacity 0.2s",
                      }}
                    >
                      ⭐
                    </button>
                  </td>
                  <td style={{ minWidth: 100 }}>{c?.classes}</td>

                  <td style={{ minWidth: 220 }}>
                    {c?.remarks ? c.remarks.slice(0, 80) + "..." : "—"}
                  </td>
                  <td style={{ minWidth: 90 }}>
                    <button
                      onClick={() => handleToggleActive(c)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                      }}
                    >
                      <StatusBadge active={c.isActive} />
                    </button>
                  </td>
                  <td style={{ minWidth: 70 }}>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        onClick={() => {
                          setEditingCourse({
                            ...c,
                            tags: Array.isArray(c.tags)
                              ? c.tags.join(", ")
                              : c.tags,
                          });
                          setShowAdd(false);
                          scrollToTop();
                        }}
                        title="Edit"
                        style={{
                          background: "none",
                          border: "none",
                          color: "var(--accent)",
                          cursor: "pointer",
                          fontSize: 15,
                        }}
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDelete(c.id)}
                        title="Delete"
                        style={{
                          background: "none",
                          border: "none",
                          color: "var(--accent2)",
                          cursor: "pointer",
                          fontSize: 15,
                        }}
                      >
                        🗑
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {courses.length === 0 && (
                <tr>
                  <td
                    colSpan={14}
                    style={{
                      textAlign: "center",
                      padding: "40px 20px",
                      color: "var(--text3)",
                    }}
                  >
                    No courses found. Add your first course above.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination sits outside the scroll wrapper */}
        <div style={{ padding: "8px 16px" }}>
          <Pagination
            page={page}
            pages={pages}
            onPage={(p) => load(p, filterCat, filterProv)}
          />
        </div>
      </div>
    </div>
  );
};

// ─── Partners Section ─────────────────────────────────────────────────────────
const PartnersSection = ({ providers, onRefresh }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const [editingPartner, setEditingPartner] = useState(null);
  const [partnerSearch, setPartnerSearch] = useState("");
  const SERVER_URL = process.env.REACT_APP_SERVER_URL;
  const [editFile, setEditFile] = useState(null);
  const [editPreview, setEditPreview] = useState(null);

  const handleEditSave = async () => {
    if (!editingPartner.name.trim()) {
      toast("Name required", "warning");
      return;
    }
    try {
      const { id, createdAt, updatedAt, coursesList, _count, ...data } =
        editingPartner;

      const fd = new FormData();
      Object.entries(data).forEach(([k, v]) => {
        if (v !== null && v !== undefined) fd.append(k, v);
      });
      if (editFile) fd.append("picLogo", editFile);

      await updateProvider(id, fd);

      setEditingPartner(null);
      onRefresh();
      toast("Partner updated! ✅", "success");
    } catch (e) {
      toast(e.response?.data?.message || "Error", "error");
    }
  };

  const handleToggleActive = async (p) => {
    try {
      await updateProvider(p.id, { isActive: !(p.isActive !== false) });
      onRefresh();
      toast("Partner status updated", "success");
    } catch {
      toast("Error", "error");
    }
  };

  const handleEditFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected) {
      setEditFile(selected);
      setEditPreview(URL.createObjectURL(selected));
    }
  };

  return (
    <div className="admin-section active">
      <div className="admin-header">
        <div className="admin-title">🤝 Education Partners</div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <input
            className="admin-input"
            style={{ margin: 0, width: 220 }}
            placeholder="🔍 Search partner by name…"
            value={partnerSearch}
            onChange={(e) => setPartnerSearch(e.target.value)}
          />
          <button
            className="btn-primary btn-sm"
            onClick={() => navigate("/admin/add-partner")}
          >
            + Add Partner
          </button>
        </div>
      </div>

      {editingPartner && (
        <div
          className="admin-card"
          style={{ marginBottom: 20, borderLeft: "3px solid var(--accent)" }}
        >
          <div
            style={{
              fontFamily: "Syne,sans-serif",
              fontWeight: 700,
              fontSize: 15,
              marginBottom: 16,
            }}
          >
            ✏️ Edit: {editingPartner.name}
          </div>
          <div className="admin-form-row">
            <div>
              <label className="admin-form-label">Partner Name *</label>
              <input
                className="admin-input"
                value={editingPartner.name}
                onChange={(e) =>
                  setEditingPartner((p) => ({ ...p, name: e.target.value }))
                }
              />
            </div>

            <div>
              <label className="admin-form-label">Logo Text (2–3 chars)</label>
              <input
                className="admin-input"
                maxLength={3}
                value={editingPartner.logo}
                onChange={(e) =>
                  setEditingPartner((p) => ({ ...p, logo: e.target.value }))
                }
              />
            </div>
          </div>

          <div className="admin-form-row">
            <div>
              <label className="admin-form-label">Brand Color</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  type="color"
                  value={editingPartner.color || "#6246ea"}
                  onChange={(e) =>
                    setEditingPartner((p) => ({ ...p, color: e.target.value }))
                  }
                  style={{
                    width: 44,
                    height: 38,
                    border: "1px solid var(--border)",
                    borderRadius: 8,
                    padding: 2,
                  }}
                />
                <input
                  className="admin-input"
                  style={{ flex: 1, margin: 0 }}
                  value={editingPartner.color}
                  onChange={(e) =>
                    setEditingPartner((p) => ({ ...p, color: e.target.value }))
                  }
                />
              </div>
            </div>
            <div>
              <label className="admin-form-label">Background Color</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  type="color"
                  value={editingPartner.bg || "#f0f2f8"}
                  onChange={(e) =>
                    setEditingPartner((p) => ({ ...p, bg: e.target.value }))
                  }
                  style={{
                    width: 44,
                    height: 38,
                    border: "1px solid var(--border)",
                    borderRadius: 8,
                    padding: 2,
                  }}
                />
                <input
                  className="admin-input"
                  style={{ flex: 1, margin: 0 }}
                  value={editingPartner.bg}
                  onChange={(e) =>
                    setEditingPartner((p) => ({ ...p, bg: e.target.value }))
                  }
                />
              </div>
            </div>
          </div>
          <div className="admin-form-row">
            <div>
              <label className="admin-form-label">Rating (0–5)</label>
              <input
                className="admin-input"
                type="number"
                step="0.1"
                min="0"
                max="5"
                value={editingPartner.rating}
                onChange={(e) =>
                  setEditingPartner((p) => ({
                    ...p,
                    rating: parseFloat(e.target.value) || 0,
                  }))
                }
              />
              {/* Logo Upload */}
              <div style={{ marginBottom: 14 }}>
                <label className="admin-form-label">Update Logo Image</label>
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--text3)",
                    marginBottom: 4,
                  }}
                >
                  PNG/JPG recommended — leave empty to keep existing
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleEditFileChange}
                />
                {/* New upload preview */}
                {editPreview && (
                  <div style={{ marginTop: 8 }}>
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--text3)",
                        marginBottom: 4,
                      }}
                    >
                      New logo:
                    </div>
                    <img
                      src={editPreview}
                      alt="new logo preview"
                      style={{
                        width: 60,
                        height: 60,
                        objectFit: "cover",
                        borderRadius: 10,
                        border: "1px solid var(--border)",
                      }}
                    />
                  </div>
                )}
                {/* Existing logo preview (jab tak naya select nahi hua) */}
                {!editPreview && editingPartner.picLogo && (
                  <div style={{ marginTop: 8 }}>
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--text3)",
                        marginBottom: 4,
                      }}
                    >
                      Current logo:
                    </div>
                    <img
                      src={`${SERVER_URL}${editingPartner.picLogo}`}
                      alt="current logo"
                      style={{
                        width: 60,
                        height: 60,
                        objectFit: "cover",
                        borderRadius: 10,
                        border: "1px solid var(--border)",
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "flex-end" }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 13,
                  cursor: "pointer",
                  paddingBottom: 10,
                }}
              >
                <input
                  type="checkbox"
                  checked={editingPartner.isActive !== false}
                  onChange={(e) =>
                    setEditingPartner((p) => ({
                      ...p,
                      isActive: e.target.checked,
                    }))
                  }
                />
                Active (visible in portal)
              </label>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            <button className="btn-primary btn-sm" onClick={handleEditSave}>
              Update Partner
            </button>
            <button
              className="btn-secondary btn-sm"
              onClick={() => setEditingPartner(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="admin-card">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Partner</th>
              <th>Logo</th>
              <th>Courses</th>
              <th>Rating</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {providers
              .filter(
                (p) =>
                  !partnerSearch ||
                  p.name.toLowerCase().includes(partnerSearch.toLowerCase()),
              )
              .map((p) => (
                <tr
                  key={p.id}
                  style={{ opacity: p.isActive !== false ? 1 : 0.5 }}
                >
                  <td>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 8,
                          background: p.bg,
                          color: p.color,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        {p.logo}
                      </div>
                      {p.name}
                    </div>
                  </td>
                  <td>
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        overflow: "hidden",
                        border: "1px solid var(--border)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#fff",
                      }}
                    >
                      {p.picLogo ? (
                        <img
                          src={`${SERVER_URL}${p.picLogo}`}
                          alt={p.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <span style={{ fontSize: 10, color: "#999" }}>N/A</span>
                      )}
                    </div>
                  </td>
                  <td>{p.courses}</td>
                  <td>⭐ {p.rating}</td>
                  <td>
                    <button
                      onClick={() => handleToggleActive(p)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                      }}
                    >
                      <StatusBadge active={p.isActive !== false} />
                    </button>
                  </td>
                  <td>
                    <button
                      onClick={() => {
                        setEditingPartner({ ...p });
                        scrollToTop();
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        color: "var(--accent)",
                        cursor: "pointer",
                        fontSize: 13,
                      }}
                    >
                      ✏️ Edit
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Domains Section ──────────────────────────────────────────────────────────
const DomainsSection = ({ categories, onDelete, onRefresh }) => {
  const toast = useToast();
  const { confirm, ConfirmComponent } = useConfirm();
  const [showAdd, setShowAdd] = useState(false);
  const [editingDomain, setEditingDomain] = useState(null);
  const [newDomain, setNewDomain] = useState({ name: "", icon: "" });

  const handleSave = async () => {
    if (!newDomain.name.trim()) {
      toast("Domain name required", "warning");
      return;
    }
    try {
      const { createCategory: cc } = await import("../services/api");
      await cc({ name: newDomain.name, icon: newDomain.icon || "📚" });
      setShowAdd(false);
      setNewDomain({ name: "", icon: "" });
      onRefresh();
      toast("Skill domain added! 🏫", "success");
    } catch (e) {
      toast(e.response?.data?.message || "Error", "error");
    }
  };

  const handleEditSave = async () => {
    if (!editingDomain.name.trim()) {
      toast("Name required", "warning");
      return;
    }
    try {
      const { id, createdAt, updatedAt, count, courses, _count, ...data } =
        editingDomain;
      await updateCategory(id, data);
      setEditingDomain(null);
      onRefresh();
      toast("Domain updated! ✅", "success");
    } catch (e) {
      toast(e.response?.data?.message || "Error", "error");
    }
  };

  const handleToggleActive = async (cat) => {
    try {
      await updateCategory(cat.id, { isActive: !(cat.isActive !== false) });
      onRefresh();
      toast("Domain status updated", "success");
    } catch {
      toast("Error", "error");
    }
  };

  const handleDeleteConfirm = async (id) => {
    const ok = await confirm(
      "Delete this skill domain? This may affect courses in this category.",
    );
    if (!ok) return;
    onDelete(id);
  };

  return (
    <div className="admin-section active">
      {ConfirmComponent}
      <div className="admin-header">
        <div className="admin-title">🏫 Skill Domains</div>
        <button
          className="btn-primary btn-sm"
          onClick={() => {
            setShowAdd((s) => !s);
            setEditingDomain(null);
          }}
        >
          + Add Domain
        </button>
      </div>

      {showAdd && !editingDomain && (
        <div className="admin-card" style={{ marginBottom: 20 }}>
          <div
            style={{
              fontFamily: "Syne,sans-serif",
              fontWeight: 700,
              fontSize: 15,
              marginBottom: 16,
            }}
          >
            ➕ Add New Skill Domain
          </div>
          <div className="admin-form-row">
            <div>
              <label className="admin-form-label">Domain Name *</label>
              <input
                className="admin-input"
                value={newDomain.name}
                onChange={(e) =>
                  setNewDomain((d) => ({ ...d, name: e.target.value }))
                }
              />
            </div>
            <div>
              <label className="admin-form-label">Icon Emoji</label>
              <input
                className="admin-input"
                maxLength={4}
                value={newDomain.icon}
                onChange={(e) =>
                  setNewDomain((d) => ({ ...d, icon: e.target.value }))
                }
              />
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            <button className="btn-primary btn-sm" onClick={handleSave}>
              Save Domain
            </button>
            <button
              className="btn-secondary btn-sm"
              onClick={() => setShowAdd(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {editingDomain && (
        <div
          className="admin-card"
          style={{ marginBottom: 20, borderLeft: "3px solid var(--accent)" }}
        >
          <div
            style={{
              fontFamily: "Syne,sans-serif",
              fontWeight: 700,
              fontSize: 15,
              marginBottom: 16,
            }}
          >
            ✏️ Edit: {editingDomain.name}
          </div>
          <div className="admin-form-row">
            <div>
              <label className="admin-form-label">Domain Name *</label>
              <input
                className="admin-input"
                value={editingDomain.name}
                onChange={(e) =>
                  setEditingDomain((d) => ({ ...d, name: e.target.value }))
                }
              />
            </div>
            <div>
              <label className="admin-form-label">Icon Emoji</label>
              <input
                className="admin-input"
                maxLength={4}
                value={editingDomain.icon}
                onChange={(e) =>
                  setEditingDomain((d) => ({ ...d, icon: e.target.value }))
                }
              />
            </div>
          </div>
          <div className="admin-form-row">
            <div>
              <label className="admin-form-label">Course Count (display)</label>
              <input
                className="admin-input"
                type="number"
                value={editingDomain.count || 0}
                onChange={(e) =>
                  setEditingDomain((d) => ({
                    ...d,
                    count: parseInt(e.target.value) || 0,
                  }))
                }
              />
            </div>
            <div style={{ display: "flex", alignItems: "flex-end" }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 13,
                  cursor: "pointer",
                  paddingBottom: 10,
                }}
              >
                <input
                  type="checkbox"
                  checked={editingDomain.isActive !== false}
                  onChange={(e) =>
                    setEditingDomain((d) => ({
                      ...d,
                      isActive: e.target.checked,
                    }))
                  }
                />
                Active (visible in portal)
              </label>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            <button className="btn-primary btn-sm" onClick={handleEditSave}>
              Update Domain
            </button>
            <button
              className="btn-secondary btn-sm"
              onClick={() => setEditingDomain(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="admin-card">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Domain</th>
              <th>Icon</th>
              <th>Courses</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((c) => (
              <tr
                key={c.id}
                style={{ opacity: c.isActive !== false ? 1 : 0.5 }}
              >
                <td>{c.name}</td>
                <td style={{ fontSize: 20 }}>{c.icon}</td>
                <td>{c.count}</td>
                <td>
                  <button
                    onClick={() => handleToggleActive(c)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                    }}
                  >
                    <StatusBadge active={c.isActive !== false} />
                  </button>
                </td>
                <td>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      onClick={() => {
                        setEditingDomain({ ...c });
                        setShowAdd(false);
                        scrollToTop();
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        color: "var(--accent)",
                        cursor: "pointer",
                        fontSize: 12,
                      }}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDeleteConfirm(c.id)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "var(--accent2)",
                        cursor: "pointer",
                        fontSize: 12,
                      }}
                    >
                      🗑
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Users Section ────────────────────────────────────────────────────────────
const UsersSection = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { confirm, ConfirmComponent } = useConfirm();

  const [enrollments, setEnrollments] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [providerFilter, setProviderFilter] = useState("");
  const [providers, setProviders] = useState([]);

  const LIMIT = 15;

  const load = useCallback((p = 1) => {
    fetchEnrollments({ limit: LIMIT, page: p, source: "manual" })
      .then((r) => {
        setEnrollments(r.data.data);
        setPage(r.data.page || p);
        setPages(r.data.pages || 1);
        setTotal(r.data.total || 0);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    load(1);
  }, [load]);

  useEffect(() => {
    const uniqueProviders = [
      ...new Set(
        enrollments.map((e) => e.course?.provider?.name).filter(Boolean),
      ),
    ];
    setProviders(uniqueProviders);
  }, [enrollments]);

  const handleDelete = async (id) => {
    const ok = await confirm("Delete this enrollment?");
    if (!ok) return;
    try {
      await deleteEnrollment(id);
      load(page);
      toast("Enrollment deleted", "info");
    } catch {
      toast("Error", "error");
    }
  };

  const statusBadge = (s) => {
    const map = {
      Active: "green",
      Completed: "blue",
      "In Progress": "orange",
      Pending: "orange",
    };
    return <span className={`admin-badge ${map[s] || "blue"}`}>{s}</span>;
  };

  const filtered = enrollments.filter((e) => {
    const q = search.toLowerCase();

    const matchesSearch =
      !q ||
      (e.name || "").toLowerCase().includes(q) ||
      (e.email || "").toLowerCase().includes(q) ||
      (e.course?.title || "").toLowerCase().includes(q);

    const matchesProvider =
      !providerFilter || e.course?.provider?.name === providerFilter;

    return matchesSearch && matchesProvider;
  });

  const handleDownloadEnrollmentsExcel = async () => {
    const result = await Swal.fire({
      title: "Download Excel?",
      text: "Are you sure you want to download enrolled users data?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Download",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#4f46e5",
      cancelButtonColor: "#d33",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      const res = await downloadEnrollmentsExcel();

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute("download", "enrollments.xlsx");

      document.body.appendChild(link);
      link.click();
      link.remove();

      Swal.fire({
        title: "Downloaded!",
        text: "Excel file downloaded successfully.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: "Failed to download Excel",
        icon: "error",
      });
    }
  };

  return (
    <div className="admin-section active">
      {ConfirmComponent}

      {/* HEADER */}
      <div
        className="admin-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          flexWrap: "nowrap",
        }}
      >
        {/* LEFT */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            onClick={handleDownloadEnrollmentsExcel}
            style={{
              background: "linear-gradient(135deg,#0ea5e9,#38bdf8)",
              color: "#fff",
              border: "none",
              padding: "8px 14px",
              borderRadius: 8,
              fontSize: 13,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
              boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
              whiteSpace: "nowrap",
            }}
          >
            ⬇ Download Excel
          </button>

          <div className="admin-title">
            👤 Enrolled Users{" "}
            <span style={{ fontSize: 13, color: "var(--text3)" }}>
              ({total} total)
            </span>
          </div>
        </div>

        {/* RIGHT */}
        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
            flexWrap: "nowrap",
          }}
        >
          <select
            value={providerFilter}
            onChange={(e) => setProviderFilter(e.target.value)}
            style={{
              fontSize: 12,
              border: "1px solid var(--border)",
              borderRadius: 6,
              padding: "6px 8px",
              background: "white",
              cursor: "pointer",
              minWidth: 140,
            }}
          >
            <option value="">All Providers</option>
            {providers.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>

          <input
            className="admin-input"
            placeholder="Search users, email, course…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              margin: 0,
              width: 220,
            }}
          />

          <button
            className="btn-primary btn-sm"
            onClick={() => navigate("/admin/add-enrollment")}
          >
            + Add User
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div className="admin-card">
        <div
          style={{
            width: "100%",
            overflowX: "auto",
          }}
        >
          <table
            className="admin-table"
            style={{
              minWidth: "1100px",
            }}
          >
            <thead>
              <tr>
                <th>#</th>
                <th>User</th>
                <th>Email</th>
                <th>Number</th>
                <th>Qualification</th>
                <th>Location</th>
                <th>Preference</th>
                <th>Source</th>
                <th>Course</th>
                <th>Provider</th>
                <th>Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((e, idx) => (
                <tr key={e.id}>
                  <td style={{ color: "var(--text3)", fontSize: 12 }}>
                    {(page - 1) * LIMIT + idx + 1}
                  </td>
                  <td>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <div className="admin-avatar">
                        {(e.name || "?")
                          .split(" ")
                          .map((n) => n[0])
                          .join("")
                          .slice(0, 2)}
                      </div>
                      {e.name}
                    </div>
                  </td>

                  <td style={{ color: "var(--text3)" }}>{e.email}</td>

                  <td style={{ color: "var(--text3)" }}>{e.phone}</td>

                  <td style={{ color: "var(--text3)" }}>
                    {e.qualification || "—"}
                  </td>

                  <td style={{ color: "var(--text3)" }}>{e.location || "—"}</td>

                  <td style={{ color: "var(--text3)" }}>
                    {e.preference || "—"}
                  </td>

                  <td>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        padding: "3px 8px",
                        borderRadius: 20,
                        background:
                          e.source === "public"
                            ? "rgba(99,102,241,0.1)"
                            : "rgba(16,185,129,0.1)",
                        color: e.source === "public" ? "#6366f1" : "#10b981",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {e.source === "public" ? "🌐 Portal" : "✏️ Manual"}
                    </span>
                  </td>

                  <td>{e.course?.title}</td>
                  <td>{e.course?.provider?.name}</td>

                  <td style={{ color: "var(--text3)" }}>
                    {new Date(e.createdAt).toLocaleDateString("en-IN")}
                  </td>

                  <td>{statusBadge(e.status)}</td>

                  <td>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        onClick={() =>
                          navigate(`/admin/edit-enrollment/${e.id}`, {
                            state: {
                              name: e.name,
                              email: e.email,
                              phone: e.phone,
                              courseId: e.courseId || e.course?.id,
                              status: e.status,
                              qualification: e.qualification,
                            },
                          })
                        }
                        style={{
                          background: "none",
                          border: "none",
                          color: "var(--accent)",
                          cursor: "pointer",
                        }}
                      >
                        ✏️
                      </button>

                      <button
                        onClick={() => handleDelete(e.id)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "var(--accent2)",
                          cursor: "pointer",
                        }}
                      >
                        🗑
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filtered.length === 0 && (
          <div
            style={{
              textAlign: "center",
              padding: "32px 0",
              color: "var(--text3)",
            }}
          >
            No enrollments found{search ? ` for "${search}"` : ""}.
          </div>
        )}

        <Pagination page={page} pages={pages} onPage={load} />
      </div>
    </div>
  );
};

// ─── Jobs Section ─────────────────────────────────────────────────────────────
const JobsSection = () => {
  const toast = useToast();
  const { confirm, ConfirmComponent } = useConfirm();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [editingJob, setEditingJob] = useState(null);
  const LIMIT = 10;
  const [companyFilter, setCompanyFilter] = useState("");

  const load = useCallback((p = 1) => {
    fetchJobs({ admin: "true", limit: LIMIT, page: p })
      .then((r) => {
        setJobs(r.data.data);
        setPage(r.data.page || p);
        setPages(r.data.pages || 1);
        setTotal(r.data.total || 0);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    load(1);
  }, [load]);

  const companies = [...new Set(jobs.map((j) => j.company).filter(Boolean))];

  const handleToggleActive = async (j) => {
    try {
      await updateJob(j.id, { isActive: !j.isActive });
      load(page);
      toast(
        j.isActive ? "Job hidden from portal" : "Job is now live!",
        "success",
      );
    } catch {
      toast("Error", "error");
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm("Delete this job posting?");
    if (!ok) return;
    try {
      const mod = await import("../services/api");
      await mod.deleteJob(id);
      load(page);
      toast("Job deleted", "info");
    } catch {
      toast("Error", "error");
    }
  };

  const handleEditSave = async () => {
    try {
      const { id, applications, createdAt, updatedAt, ...data } = editingJob;
      if (typeof data.tags === "string")
        data.tags = data.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean);
      await updateJob(id, data);
      setEditingJob(null);
      load(page);
      toast("Job updated! ✅", "success");
    } catch (e) {
      toast(e.response?.data?.message || "Error", "error");
    }
  };

  const filteredJobs = companyFilter
    ? jobs.filter((j) => j.company === companyFilter)
    : jobs;

  return (
    <div className="admin-section active">
      {ConfirmComponent}

      <div className="admin-header">
        <div className="admin-title">
          💼 Job Postings{" "}
          <span
            style={{ fontSize: 13, fontWeight: 400, color: "var(--text3)" }}
          >
            ({total} total)
          </span>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div>
            <select
              className="admin-select"
              style={{ margin: 0, minWidth: 160 }}
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
            >
              <option value="">All Companies</option>
              {companies.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <button
            className="btn-primary btn-sm"
            style={{ background: "#f97316" }}
            onClick={() => navigate("/admin/post-job")}
          >
            + Post New Job
          </button>
        </div>
      </div>

      {editingJob && (
        <div
          className="admin-card"
          style={{ marginBottom: 20, borderLeft: "3px solid #f97316" }}
        >
          <div
            style={{
              fontFamily: "Syne,sans-serif",
              fontWeight: 700,
              fontSize: 15,
              marginBottom: 16,
            }}
          >
            ✏️ Edit: {editingJob.title}
          </div>
          <div className="admin-form-row">
            <div>
              <label className="admin-form-label">Job Title *</label>
              <input
                className="admin-input"
                value={editingJob.title}
                onChange={(e) =>
                  setEditingJob((j) => ({ ...j, title: e.target.value }))
                }
              />
            </div>
            <div>
              <label className="admin-form-label">Company</label>
              <input
                className="admin-input"
                value={editingJob.company}
                onChange={(e) =>
                  setEditingJob((j) => ({ ...j, company: e.target.value }))
                }
              />
            </div>
          </div>
          <div className="admin-form-row">
            <div>
              <label className="admin-form-label">Category</label>
              <input
                className="admin-input"
                value={editingJob.cat}
                onChange={(e) =>
                  setEditingJob((j) => ({ ...j, cat: e.target.value }))
                }
              />
            </div>
            <div>
              <label className="admin-form-label">Job Type</label>
              <select
                className="admin-select"
                value={editingJob.jobType}
                onChange={(e) =>
                  setEditingJob((j) => ({ ...j, jobType: e.target.value }))
                }
              >
                {[
                  "fulltime",
                  "parttime",
                  "remote",
                  "hybrid",
                  "internship",
                  "contract",
                ].map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="admin-form-row">
            <div>
              <label className="admin-form-label">Location</label>
              <input
                className="admin-input"
                value={editingJob.location}
                onChange={(e) =>
                  setEditingJob((j) => ({ ...j, location: e.target.value }))
                }
              />
            </div>
            <div>
              <label className="admin-form-label">Salary</label>
              <input
                className="admin-input"
                value={editingJob.salary}
                onChange={(e) =>
                  setEditingJob((j) => ({ ...j, salary: e.target.value }))
                }
              />
            </div>
          </div>
          <label className="admin-form-label">Tags (comma separated)</label>
          <input
            className="admin-input"
            value={
              Array.isArray(editingJob.tags)
                ? editingJob.tags.join(", ")
                : editingJob.tags
            }
            onChange={(e) =>
              setEditingJob((j) => ({ ...j, tags: e.target.value }))
            }
          />
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              cursor: "pointer",
              marginTop: 12,
            }}
          >
            <input
              type="checkbox"
              checked={!!editingJob.isActive}
              onChange={(e) =>
                setEditingJob((j) => ({ ...j, isActive: e.target.checked }))
              }
            />
            Active (visible in portal)
          </label>
          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            <button className="btn-primary btn-sm" onClick={handleEditSave}>
              Update Job
            </button>
            <button
              className="btn-secondary btn-sm"
              onClick={() => setEditingJob(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="admin-card">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Job Title</th>
              <th>Company</th>
              <th>Type</th>
              <th>Location</th>
              <th>Apps</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredJobs.map((j) => (
              <tr key={j.id} style={{ opacity: j.isActive ? 1 : 0.5 }}>
                <td>
                  <strong>{j.title}</strong>
                  <div style={{ fontSize: 11, color: "var(--text3)" }}>
                    {j.cat} · {j.expLevel}
                  </div>
                </td>
                <td>{j.company}</td>
                <td>{j.jobType}</td>
                <td style={{ color: "var(--text3)" }}>{j.location}</td>
                <td>{j.applicantCount ?? j.applications?.length ?? 0}</td>
                <td>
                  <button
                    onClick={() => handleToggleActive(j)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                    }}
                  >
                    <StatusBadge active={j.isActive} />
                  </button>
                </td>
                <td>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      onClick={() => {
                        setEditingJob({
                          ...j,
                          tags: Array.isArray(j.tags)
                            ? j.tags.join(", ")
                            : j.tags,
                        });
                        scrollToTop();
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        color: "var(--accent)",
                        cursor: "pointer",
                        fontSize: 13,
                      }}
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDelete(j.id)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "var(--accent2)",
                        cursor: "pointer",
                        fontSize: 13,
                      }}
                    >
                      🗑
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {jobs.length === 0 && (
          <div
            style={{
              textAlign: "center",
              color: "var(--text3)",
              padding: "40px 0",
              fontSize: 14,
            }}
          >
            No jobs yet. Click "+ Post New Job".
          </div>
        )}
        <Pagination page={page} pages={pages} onPage={load} />
      </div>
    </div>
  );
};

// ─── Applications Section (NEW) ───────────────────────────────────────────────
const ApplicationsSection = () => {
  const toast = useToast();
  const { confirm, ConfirmComponent } = useConfirm();
  const [applications, setApplications] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [filterQual, setFilterQual] = useState("");
  const [filterExp, setFilterExp] = useState("");
  const LIMIT = 15;
  const API_BASE =
    process.env.REACT_APP_SERVER_URL || "https://skillyer-api.onrender.com";

  const load = useCallback((p = 1) => {
    fetchApplications({ limit: LIMIT, page: p })
      .then((r) => {
        setApplications(r.data.data);
        setPage(r.data.page || p);
        setPages(r.data.pages || 1);
        setTotal(r.data.total || 0);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    load(1);
  }, [load]);

  const handleStatusChange = async (id, status) => {
    try {
      await updateApplicationStatus(id, status);
      setApplications((prev) =>
        prev.map((a) => (a.id === id ? { ...a, status } : a)),
      );
      toast(`Status updated to "${status}"`, "success");
    } catch {
      toast("Error", "error");
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm("Delete this application and its CV?");
    if (!ok) return;
    try {
      await deleteApplication(id);
      load(page);
      toast("Application deleted", "info");
    } catch {
      toast("Error", "error");
    }
  };

  const handleDownloadExcel = async () => {
    const result = await Swal.fire({
      title: "Download Excel?",
      text: "Are you sure you want to download all applications data?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Download",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#4f46e5",
      cancelButtonColor: "#d33",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      const res = await downloadApplicationsExcel();

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute("download", "applications.xlsx");

      document.body.appendChild(link);
      link.click();
      link.remove();

      Swal.fire({
        title: "Downloaded!",
        text: "Excel file downloaded successfully.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: "Failed to download Excel",
        icon: "error",
      });
    }
  };

  const statusColor = {
    Pending: "orange",
    Reviewed: "blue",
    Shortlisted: "green",
    Rejected: "orange",
  };

  const filtered = applications.filter((a) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      (a.name || "").toLowerCase().includes(q) ||
      (a.email || "").toLowerCase().includes(q) ||
      (a.job?.title || "").toLowerCase().includes(q) ||
      (a.job?.company || "").toLowerCase().includes(q);
    const matchQual = !filterQual || (a.qualification || "") === filterQual;
    const matchExp = !filterExp || (a.experience || "") === filterExp;
    return matchSearch && matchQual && matchExp;
  });

  return (
    <div className="admin-section active">
      {ConfirmComponent}

      <div style={{ marginBottom: 24 }}>
        {/* Row 1: Title + Download */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 10,
            marginBottom: 12,
          }}
        >
          <div className="admin-title">
            📋 Job Applications{" "}
            <span
              style={{ fontSize: 13, fontWeight: 400, color: "var(--text3)" }}
            >
              ({total} total)
            </span>
          </div>
          <button
            onClick={handleDownloadExcel}
            style={{
              background: "linear-gradient(135deg,#4f46e5,#6366f1)",
              color: "#fff",
              border: "none",
              padding: "8px 14px",
              borderRadius: 8,
              fontSize: 13,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
              boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
            }}
          >
            ⬇ Download Excel
          </button>
        </div>

        {/* Row 2: Search + Filters */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <input
            className="admin-input"
            style={{ margin: 0, width: 220 }}
            placeholder="Search by name, job, company…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className="admin-select"
            style={{ margin: 0, minWidth: 160 }}
            value={filterQual}
            onChange={(e) => setFilterQual(e.target.value)}
          >
            <option value="">🎓 All Qualifications</option>
            <option value="10th Pass">10th Pass</option>
            <option value="12th Pass">12th Pass</option>
            <option value="Diploma">Diploma</option>
            <option value="B.Tech/B.E.">B.Tech/B.E.</option>
            <option value="B.Sc">B.Sc</option>
            <option value="B.Com">B.Com</option>
            <option value="B.A">B.A</option>
            <option value="BCA">BCA</option>
            <option value="MCA">MCA</option>
            <option value="M.Tech/M.E.">M.Tech/M.E.</option>
            <option value="MBA">MBA</option>
            <option value="M.Sc">M.Sc</option>
            <option value="Ph.D">Ph.D</option>
            <option value="Other">Other</option>
          </select>
          <select
            className="admin-select"
            style={{ margin: 0, minWidth: 160 }}
            value={filterExp}
            onChange={(e) => setFilterExp(e.target.value)}
          >
            <option value="">💼 All Experience</option>
            <option value="Fresher (0 yr)">Fresher (0 yr)</option>
            <option value="Less than 1 yr">Less than 1 yr</option>
            <option value="1 year">1 year</option>
            <option value="2 years">2 years</option>
            <option value="3 years">3 years</option>
            <option value="4 years">4 years</option>
            <option value="5 years">5 years</option>
            <option value="6-8 years">6-8 years</option>
            <option value="9-12 years">9-12 years</option>
            <option value="12+ years">12+ years</option>
          </select>
        </div>
      </div>

      <div className="admin-card" style={{ overflowX: "auto" }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Applicant</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Job</th>
              <th>Company</th>
              <th>Qualification</th>
              <th>Experience</th>
              <th>Current Org</th>
              <th>CTC</th>
              <th>ECTC</th>
              <th>Notice</th>
              <th>CV/Resume</th>
              <th>Date</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((a) => (
              <tr key={a.id}>
                <td>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <div className="admin-avatar">
                      {(a.name || "?")
                        .split(" ")
                        .map((n) => n[0])
                        .join("")
                        .slice(0, 2)}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600 }}>{a.name}</div>
                      {a.message && (
                        <div
                          style={{
                            fontSize: 11,
                            color: "var(--text3)",
                            maxWidth: 140,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {a.message}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td style={{ color: "var(--text3)" }}>{a.email}</td>
                <td style={{ color: "var(--text3)" }}>{a.phone || "—"}</td>
                <td>
                  <strong>{a.job?.title || "—"}</strong>
                </td>
                <td>{a.job?.company || "—"}</td>
                <td style={{ color: "var(--text3)", whiteSpace: "nowrap" }}>
                  {a.qualification || "—"}
                </td>
                <td style={{ color: "var(--text3)", whiteSpace: "nowrap" }}>
                  {a.experience || "—"}
                </td>
                <td style={{ color: "var(--text3)", whiteSpace: "nowrap" }}>
                  {a.currentOrg || "—"}
                </td>
                <td style={{ color: "var(--text3)", whiteSpace: "nowrap" }}>
                  {a.currentCtc || "—"}
                </td>
                <td style={{ color: "var(--text3)", whiteSpace: "nowrap" }}>
                  {a.expectedCtc || "—"}
                </td>
                <td style={{ color: "var(--text3)", whiteSpace: "nowrap" }}>
                  {a.noticePeriod || "—"}
                </td>
                <td>
                  {a.cvUrl ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                      }}
                    >
                      <a
                        href={`${API_BASE}${a.cvUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "var(--accent)",
                          fontSize: 12,
                          textDecoration: "none",
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        👁 View
                      </a>
                      <a
                        href={`${API_BASE}${a.cvUrl}`}
                        download={a.cvName || "resume"}
                        style={{
                          color: "var(--text2)",
                          fontSize: 12,
                          textDecoration: "none",
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        ⬇ Download
                      </a>
                    </div>
                  ) : (
                    <span style={{ color: "var(--text3)", fontSize: 12 }}>
                      No CV
                    </span>
                  )}
                </td>
                <td style={{ color: "var(--text3)" }}>
                  {new Date(a.createdAt).toLocaleDateString("en-IN")}
                </td>
                <td>
                  <select
                    value={a.status}
                    onChange={(e) => handleStatusChange(a.id, e.target.value)}
                    style={{
                      fontSize: 11,
                      border: "1px solid var(--border)",
                      borderRadius: 6,
                      padding: "3px 6px",
                      background: "white",
                      cursor: "pointer",
                    }}
                  >
                    {["Pending", "Reviewed", "Shortlisted", "Rejected"].map(
                      (s) => (
                        <option key={s}>{s}</option>
                      ),
                    )}
                  </select>
                </td>
                <td>
                  <button
                    onClick={() => handleDelete(a.id)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "var(--accent2)",
                      cursor: "pointer",
                      fontSize: 13,
                    }}
                  >
                    🗑
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div
            style={{
              textAlign: "center",
              padding: "32px 0",
              color: "var(--text3)",
            }}
          >
            No applications yet.
          </div>
        )}
        <Pagination page={page} pages={pages} onPage={load} />
      </div>
    </div>
  );
};

// ─── Course Enrollments Section (Public — from EnrollModal) ──────────────────
const CourseEnrollmentsSection = () => {
  const toast = useToast();
  const { confirm, ConfirmComponent } = useConfirm();
  const [enrollments, setEnrollments] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const LIMIT = 15;

  const load = useCallback((p = 1) => {
    fetchEnrollments({ limit: LIMIT, page: p, source: "public" })
      .then((r) => {
        setEnrollments(r.data.data);
        setPage(r.data.page || p);
        setPages(r.data.pages || 1);
        setTotal(r.data.total || 0);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    load(1);
  }, [load]);

  const handleDelete = async (id) => {
    const ok = await confirm("Delete this enrollment?");
    if (!ok) return;
    try {
      await deleteEnrollment(id);
      load(page);
      toast("Enrollment deleted", "info");
    } catch {
      toast("Error", "error");
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await updateEnrollmentStatus(id, status);
      setEnrollments((prev) =>
        prev.map((e) => (e.id === id ? { ...e, status } : e)),
      );
      toast(`Status updated to "${status}"`, "success");
    } catch {
      toast("Error", "error");
    }
  };

  const filtered = enrollments.filter((e) => {
    const q = search.toLowerCase();
    return (
      !q ||
      (e.name || "").toLowerCase().includes(q) ||
      (e.email || "").toLowerCase().includes(q) ||
      (e.course?.title || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="admin-section active">
      {ConfirmComponent}
      <div className="admin-header">
        <div className="admin-title">
          🎓 Enrolled (Public){" "}
          <span
            style={{ fontSize: 13, fontWeight: 400, color: "var(--text3)" }}
          >
            ({total} total — submitted via course page)
          </span>
        </div>
        <input
          className="admin-input"
          style={{ margin: 0, width: 220 }}
          placeholder="Search name, email, course…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div className="admin-card" style={{ overflowX: "auto" }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Qualification</th>
              <th>Course</th>
              <th>Provider</th>
              <th>Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e) => (
              <tr key={e.id}>
                <td>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <div className="admin-avatar">
                      {(e.name || "?")
                        .split(" ")
                        .map((n) => n[0])
                        .join("")
                        .slice(0, 2)}
                    </div>
                    {e.name}
                  </div>
                </td>
                <td style={{ color: "var(--text3)" }}>{e.email}</td>
                <td style={{ color: "var(--text3)" }}>{e.phone || "—"}</td>
                <td style={{ color: "var(--text3)", whiteSpace: "nowrap" }}>
                  {e.qualification || "—"}
                </td>
                <td>{e.course?.title || "—"}</td>
                <td>{e.course?.provider?.name || "—"}</td>
                <td style={{ color: "var(--text3)" }}>
                  {new Date(e.createdAt).toLocaleDateString("en-IN")}
                </td>
                <td>
                  <select
                    value={e.status}
                    onChange={(ev) => handleStatusChange(e.id, ev.target.value)}
                    style={{
                      fontSize: 11,
                      border: "1px solid var(--border)",
                      borderRadius: 6,
                      padding: "3px 6px",
                      background: "white",
                      cursor: "pointer",
                    }}
                  >
                    {["Active", "In Progress", "Completed"].map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <button
                    onClick={() => handleDelete(e.id)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "var(--accent2)",
                      cursor: "pointer",
                      fontSize: 13,
                    }}
                  >
                    🗑
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div
            style={{
              textAlign: "center",
              padding: "32px 0",
              color: "var(--text3)",
              fontSize: 13,
            }}
          >
            No public enrollments found{search ? ` for "${search}"` : ""}.
          </div>
        )}
        <Pagination page={page} pages={pages} onPage={load} />
      </div>
    </div>
  );
};

// ─── Counselling Section ──────────────────────────────────────────────────────
const CounselSection = () => {
  const toast = useToast();
  const [counsels, setCounsels] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const LIMIT = 15;
  const [fieldFilter, setFieldFilter] = useState("");
  const [fields, setFields] = useState([]);

  const load = useCallback((p = 1) => {
    fetchCounselRequests({ limit: LIMIT, page: p })
      .then((r) => {
        setCounsels(r.data.data);
        setPage(r.data.page || p);
        setPages(r.data.pages || 1);
        setTotal(r.data.total || 0);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    load(1);
  }, [load]);

  useEffect(() => {
    const uniqueFields = [
      ...new Set(counsels.map((c) => c.field).filter(Boolean)),
    ];
    setFields(uniqueFields);
  }, [counsels]);

  const filteredCounsels = fieldFilter
    ? counsels.filter((c) => c.field === fieldFilter)
    : counsels;

  const handleStatusChange = async (id, value) => {
    try {
      await updateCounselStatus(id, value);
      setCounsels((prev) =>
        prev.map((x) => (x.id === id ? { ...x, status: value } : x)),
      );
      toast(`Status → "${value}"`, "success");
    } catch {
      toast("Error", "error");
    }
  };

  const handleDownloadCounselExcel = async () => {
    const result = await Swal.fire({
      title: "Download Excel?",
      text: "Are you sure you want to download counselling data?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Download",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#4f46e5",
      cancelButtonColor: "#d33",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      const res = await downloadCounselExcel();

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute("download", "counsel_requests.xlsx");

      document.body.appendChild(link);
      link.click();
      link.remove();

      Swal.fire({
        title: "Downloaded!",
        text: "Excel file downloaded successfully.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: "Failed to download Excel",
        icon: "error",
      });
    }
  };

  return (
    <div className="admin-section active">
      <div className="admin-header">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            onClick={handleDownloadCounselExcel}
            style={{
              background: "linear-gradient(135deg,#059669,#10b981)",
              color: "#fff",
              border: "none",
              padding: "8px 14px",
              borderRadius: 8,
              fontSize: 13,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
              boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
            }}
          >
            ⬇ Download Excel
          </button>

          <div className="admin-title">
            🧑‍💼 Counselling Requests{" "}
            <span
              style={{ fontSize: 13, fontWeight: 400, color: "var(--text3)" }}
            >
              ({total} total)
            </span>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <select
            value={fieldFilter}
            onChange={(e) => setFieldFilter(e.target.value)}
            style={{
              fontSize: 12,
              border: "1px solid var(--border)",
              borderRadius: 6,
              padding: "5px 8px",
              background: "white",
              cursor: "pointer",
            }}
          >
            <option value="">All Fields</option>
            {fields.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="admin-card">
        <table className="admin-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Field</th>
              <th>Exp Level</th>
              <th>Message</th>
              <th>Date</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredCounsels.map((c, idx) => (
              <tr key={c.id}>
                <td style={{ color: "var(--text3)", fontSize: 12 }}>
                  {(page - 1) * LIMIT + idx + 1}
                </td>
                <td>{c.name}</td>
                <td style={{ color: "var(--text3)" }}>{c.email}</td>
                <td style={{ color: "var(--text3)" }}>{c.phone}</td>
                <td>{c.field}</td>
                <td>{c.expLevel}</td>
                <td
                  style={{
                    maxWidth: 180,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    color: "var(--text3)",
                  }}
                >
                  {c.message}
                </td>
                <td style={{ color: "var(--text3)" }}>
                  {new Date(c.createdAt).toLocaleDateString("en-IN")}
                </td>
                <td>
                  <select
                    value={c.status}
                    onChange={(e) => handleStatusChange(c.id, e.target.value)}
                    style={{
                      fontSize: 11,
                      border: "1px solid var(--border)",
                      borderRadius: 6,
                      padding: "3px 6px",
                      background: "white",
                      cursor: "pointer",
                    }}
                  >
                    {["Pending", "Scheduled", "Completed"].map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination page={page} pages={pages} onPage={load} />
      </div>
    </div>
  );
};

// ─── Testimonials Section ─────────────────────────────────────────────────────
const EMPTY_TESTI = {
  name: "",
  role: "",
  company: "",
  avatar: "",
  avatarBg: "#6246ea",
  avatarCo: "#fff",
  course: "",
  text: "",
  rating: 5,
};

const TestimonialsSection = () => {
  const toast = useToast();
  const { confirm, ConfirmComponent } = useConfirm();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingT, setEditingT] = useState(null);
  const [form, setForm] = useState(EMPTY_TESTI);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchTestimonialsAdmin();
      setTestimonials(r.data.data || []);
    } catch {
      toast.error("Failed to load testimonials");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleAdd = async () => {
    if (
      !form.name.trim() ||
      !form.role.trim() ||
      !form.company.trim() ||
      !form.avatar.trim() ||
      !form.course.trim() ||
      !form.text.trim()
    ) {
      toast.error("Please fill all required fields");
      return;
    }
    try {
      await createTestimonial(form);
      toast.success("Testimonial added!");
      setShowAdd(false);
      setForm(EMPTY_TESTI);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Error adding testimonial");
    }
  };

  const handleEditSave = async () => {
    try {
      await updateTestimonial(editingT.id, editingT);
      toast.success("Testimonial updated!");
      setEditingT(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Error updating testimonial");
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: "Delete testimonial?",
      message: "This cannot be undone.",
      confirmText: "Delete",
    });
    if (!ok) return;
    try {
      await deleteTestimonial(id);
      toast.success("Deleted!");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleToggleActive = async (t) => {
    try {
      await updateTestimonial(t.id, { isActive: !t.isActive });
      load();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const TestiForm = ({ data, onChange }) => (
    <div>
      <div className="form-row" style={{ marginBottom: 12 }}>
        <div>
          <label className="form-label">Name *</label>
          <input
            className="form-input"
            placeholder="e.g. Priya Sharma"
            value={data.name}
            onChange={(e) => onChange({ ...data, name: e.target.value })}
          />
        </div>
        <div>
          <label className="form-label">Role *</label>
          <input
            className="form-input"
            placeholder="e.g. Data Analyst"
            value={data.role}
            onChange={(e) => onChange({ ...data, role: e.target.value })}
          />
        </div>
      </div>
      <div className="form-row" style={{ marginBottom: 12 }}>
        <div>
          <label className="form-label">Company *</label>
          <input
            className="form-input"
            placeholder="e.g. Razorpay"
            value={data.company}
            onChange={(e) => onChange({ ...data, company: e.target.value })}
          />
        </div>
        <div>
          <label className="form-label">Avatar Initials *</label>
          <input
            className="form-input"
            placeholder="e.g. PS"
            maxLength={3}
            value={data.avatar}
            onChange={(e) => onChange({ ...data, avatar: e.target.value })}
          />
        </div>
      </div>
      <div className="form-row" style={{ marginBottom: 12 }}>
        <div>
          <label className="form-label">Avatar Background Color</label>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="color"
              value={data.avatarBg}
              onChange={(e) => onChange({ ...data, avatarBg: e.target.value })}
              style={{
                width: 40,
                height: 36,
                border: "none",
                cursor: "pointer",
                borderRadius: 6,
              }}
            />
            <input
              className="form-input"
              value={data.avatarBg}
              onChange={(e) => onChange({ ...data, avatarBg: e.target.value })}
            />
          </div>
        </div>
        <div>
          <label className="form-label">Avatar Text Color</label>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="color"
              value={data.avatarCo}
              onChange={(e) => onChange({ ...data, avatarCo: e.target.value })}
              style={{
                width: 40,
                height: 36,
                border: "none",
                cursor: "pointer",
                borderRadius: 6,
              }}
            />
            <input
              className="form-input"
              value={data.avatarCo}
              onChange={(e) => onChange({ ...data, avatarCo: e.target.value })}
            />
          </div>
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label className="form-label">Course Name *</label>
        <input
          className="form-input"
          placeholder="e.g. Machine Learning Specialization"
          value={data.course}
          onChange={(e) => onChange({ ...data, course: e.target.value })}
        />
      </div>
      <div style={{ marginBottom: 12 }}>
        <label className="form-label">Testimonial Text *</label>
        <textarea
          className="form-input"
          rows={4}
          placeholder="What did the learner say..."
          value={data.text}
          onChange={(e) => onChange({ ...data, text: e.target.value })}
          style={{ resize: "vertical" }}
        />
      </div>
      <div style={{ marginBottom: 4 }}>
        <label className="form-label">Rating (1–5)</label>
        <select
          className="form-input"
          value={data.rating}
          onChange={(e) =>
            onChange({ ...data, rating: Number(e.target.value) })
          }
        >
          {[5, 4, 3, 2, 1].map((n) => (
            <option key={n} value={n}>
              {n} ★
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  return (
    <div className="admin-section active">
      {ConfirmComponent}
      <div className="admin-header">
        <div className="admin-title">
          💬 Testimonials{" "}
          <span
            style={{ fontSize: 13, fontWeight: 400, color: "var(--text3)" }}
          >
            ({testimonials.length} total)
          </span>
        </div>
        <button
          className="btn-primary btn-sm"
          onClick={() => {
            setShowAdd((s) => !s);
            setEditingT(null);
          }}
        >
          + Add Testimonial
        </button>
      </div>

      {showAdd && !editingT && (
        <div className="admin-card" style={{ marginBottom: 20 }}>
          <div
            style={{
              fontFamily: "Syne,sans-serif",
              fontWeight: 700,
              fontSize: 15,
              marginBottom: 16,
            }}
          >
            ➕ Add New Testimonial
          </div>
          <TestiForm data={form} onChange={setForm} />
          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            <button className="btn-primary btn-sm" onClick={handleAdd}>
              Save Testimonial
            </button>
            <button
              className="btn-secondary btn-sm"
              onClick={() => setShowAdd(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {editingT && (
        <div
          className="admin-card"
          style={{ marginBottom: 20, borderLeft: "3px solid var(--accent)" }}
        >
          <div
            style={{
              fontFamily: "Syne,sans-serif",
              fontWeight: 700,
              fontSize: 15,
              marginBottom: 16,
            }}
          >
            ✏️ Edit: {editingT.name}
          </div>
          <TestiForm data={editingT} onChange={setEditingT} />
          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            <button className="btn-primary btn-sm" onClick={handleEditSave}>
              Update Testimonial
            </button>
            <button
              className="btn-secondary btn-sm"
              onClick={() => setEditingT(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div
          className="admin-card"
          style={{ textAlign: "center", padding: 32, color: "var(--text3)" }}
        >
          Loading...
        </div>
      ) : testimonials.length === 0 ? (
        <div
          className="admin-card"
          style={{ textAlign: "center", padding: 40, color: "var(--text3)" }}
        >
          <div style={{ fontSize: 40, marginBottom: 12 }}>💬</div>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            No testimonials yet
          </div>
          <div style={{ fontSize: 13 }}>
            Add your first testimonial using the button above.
          </div>
        </div>
      ) : (
        <div className="admin-card">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Person</th>
                <th>Company</th>
                <th>Course</th>
                <th>Rating</th>
                <th>Text</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {testimonials.map((t) => (
                <tr key={t.id} style={{ opacity: t.isActive ? 1 : 0.5 }}>
                  <td>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          background: t.avatarBg,
                          color: t.avatarCo,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 11,
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      >
                        {t.avatar}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>
                          {t.name}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--text3)" }}>
                          {t.role}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>{t.company}</td>
                  <td
                    style={{
                      maxWidth: 140,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: 12,
                      color: "var(--text3)",
                    }}
                  >
                    {t.course}
                  </td>
                  <td>{"★".repeat(t.rating)}</td>
                  <td
                    style={{
                      maxWidth: 200,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: "var(--text3)",
                      fontSize: 12,
                    }}
                  >
                    {t.text}
                  </td>
                  <td>
                    <button
                      onClick={() => handleToggleActive(t)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                      }}
                    >
                      <StatusBadge active={t.isActive} />
                    </button>
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        onClick={() => {
                          setEditingT({ ...t });
                          setShowAdd(false);
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          color: "var(--accent)",
                          cursor: "pointer",
                          fontSize: 13,
                        }}
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDelete(t.id)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "var(--accent2)",
                          cursor: "pointer",
                          fontSize: 13,
                        }}
                      >
                        🗑
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Registered Users Section ─────────────────────────────────────────────────
const RegisteredUsersSection = () => {
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);

  const load = useCallback(
    async (p = 1, q = search) => {
      setLoading(true);
      try {
        const r = await fetchAllUsers({ page: p, limit: 20, search: q });
        setUsers(r.data.users);
        setTotal(r.data.total);
        setPages(r.data.pages);
        setPage(r.data.page);
      } catch {
        toast("Failed to load users", "error");
      } finally {
        setLoading(false);
      }
    },
    [search, toast],
  );

  useEffect(() => {
    load(1);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    load(1, search);
  };

  const handleToggle = async (id) => {
    try {
      const r = await toggleUserStatus(id);
      setUsers((prev) =>
        prev.map((u) =>
          u.id === id ? { ...u, isActive: r.data.user.isActive } : u,
        ),
      );
      toast(
        r.data.user.isActive
          ? "User activated successfully"
          : "User deactivated successfully",
        "success",
      );
    } catch {
      toast("Failed to update status", "error");
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`"${name}"  - confirm delete?`)) return;
    try {
      await deleteUser(id);
      setUsers((prev) => prev.filter((u) => u.id !== id));
      setTotal((t) => t - 1);
      toast("User deleted successfully", "info");
    } catch {
      toast("Failed to delete user", "error");
    }
  };

  const fmtDate = (d) =>
    new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  return (
    <div className="admin-section active">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 20,
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>
            🧑‍💻 Registered Users
          </h2>
          <p style={{ margin: "4px 0 0", color: "var(--text3)", fontSize: 13 }}>
            Total {total} registered users
          </p>
        </div>
        <form onSubmit={handleSearch} style={{ display: "flex", gap: 8 }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email or phone...…"
            style={{
              border: "1.5px solid var(--border)",
              borderRadius: 8,
              padding: "8px 14px",
              fontSize: 13,
              minWidth: 240,
              color: "var(--text1)",
              background: "var(--bg1)",
            }}
          />
          <button
            type="submit"
            style={{
              background: "var(--accent1)",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "8px 16px",
              fontSize: 13,
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            🔍 Search
          </button>
          {search && (
            <button
              type="button"
              onClick={() => {
                setSearch("");
                load(1, "");
              }}
              style={{
                background: "var(--bg2)",
                border: "1px solid var(--border)",
                borderRadius: 8,
                padding: "8px 12px",
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              ✕ Clear
            </button>
          )}
        </form>
      </div>

      {loading ? (
        <div
          style={{ textAlign: "center", padding: 60, color: "var(--text3)" }}
        >
          ⏳ Loading...
        </div>
      ) : users.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: 60,
            color: "var(--text3)",
            fontSize: 15,
          }}
        >
          {search
            ? "No users found matching your search"
            : "No users have registered yet"}
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 13,
            }}
          >
            <thead>
              <tr style={{ background: "var(--bg2)" }}>
                {[
                  "#",
                  "Naam",
                  "Email",
                  "Phone",
                  "Registered On",
                  "Status",
                  "Actions",
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: "10px 14px",
                      textAlign: "left",
                      fontWeight: 600,
                      color: "var(--text2)",
                      borderBottom: "1px solid var(--border)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr
                  key={u.id}
                  style={{
                    borderBottom: "1px solid var(--border)",
                    background: i % 2 === 0 ? "var(--bg1)" : "var(--bg2)",
                  }}
                >
                  <td style={{ padding: "10px 14px", color: "var(--text3)" }}>
                    {(page - 1) * 20 + i + 1}
                  </td>
                  <td
                    style={{
                      padding: "10px 14px",
                      fontWeight: 600,
                      color: "var(--text1)",
                    }}
                  >
                    {u.name}
                  </td>
                  <td style={{ padding: "10px 14px", color: "var(--text2)" }}>
                    {u.email}
                  </td>
                  <td style={{ padding: "10px 14px", color: "var(--text2)" }}>
                    {u.phone || "—"}
                  </td>
                  <td
                    style={{
                      padding: "10px 14px",
                      color: "var(--text3)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {fmtDate(u.createdAt)}
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    <span
                      style={{
                        background: u.isActive ? "#d1fae5" : "#fee2e2",
                        color: u.isActive ? "#065f46" : "#991b1b",
                        borderRadius: 20,
                        padding: "3px 10px",
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      {u.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    <div
                      style={{ display: "flex", gap: 8, alignItems: "center" }}
                    >
                      <button
                        onClick={() => handleToggle(u.id)}
                        title={u.isActive ? "Deactivate" : "Activate"}
                        style={{
                          background: u.isActive ? "#fef3c7" : "#d1fae5",
                          color: u.isActive ? "#92400e" : "#065f46",
                          border: "none",
                          borderRadius: 6,
                          padding: "4px 10px",
                          fontSize: 12,
                          cursor: "pointer",
                          fontWeight: 600,
                        }}
                      >
                        {u.isActive ? "Deactivate" : "Activate"}
                      </button>
                      <button
                        onClick={() => handleDelete(u.id, u.name)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "var(--accent2)",
                          cursor: "pointer",
                          fontSize: 14,
                        }}
                        title="Delete"
                      >
                        🗑
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {pages > 1 && (
            <div
              style={{
                display: "flex",
                gap: 6,
                justifyContent: "center",
                marginTop: 20,
                flexWrap: "wrap",
              }}
            >
              <button
                onClick={() => load(page - 1)}
                disabled={page <= 1}
                style={{
                  padding: "5px 12px",
                  borderRadius: 7,
                  border: "1px solid var(--border)",
                  background: page <= 1 ? "var(--bg2)" : "white",
                  cursor: page <= 1 ? "not-allowed" : "pointer",
                  fontSize: 13,
                }}
              >
                ← Prev
              </button>
              {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => load(p)}
                  style={{
                    padding: "5px 12px",
                    borderRadius: 7,
                    border: "1px solid var(--border)",
                    background: p === page ? "var(--accent1)" : "white",
                    color: p === page ? "#fff" : "var(--text1)",
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: p === page ? 700 : 400,
                  }}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => load(page + 1)}
                disabled={page >= pages}
                style={{
                  padding: "5px 12px",
                  borderRadius: 7,
                  border: "1px solid var(--border)",
                  background: page >= pages ? "var(--bg2)" : "white",
                  cursor: page >= pages ? "not-allowed" : "pointer",
                  fontSize: 13,
                }}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Admin Dashboard ──────────────────────────────────────────────────────────
const AdminDashboard = () => {
  const toast = useToast();
  const { confirm, ConfirmComponent } = useConfirm();
  const location = useLocation();
  const navigate = useNavigate();

  const [section, setSection] = useState(
    location.state?.section || "dashboard",
  );
  const [stats, setStats] = useState({});
  const [categories, setCategories] = useState([]);
  const [providers, setProviders] = useState([]);

  useEffect(() => {
    if (location.state?.section) {
      setSection(location.state.section);
      navigate("/admin", { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  useEffect(() => {
    fetchAdminStats()
      .then((r) => setStats(r.data.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (["courses", "partners", "domains"].includes(section)) {
      fetchCategories()
        .then((r) => setCategories(r.data.data))
        .catch(() => {});
      fetchProviders()
        .then((r) => setProviders(r.data.data))
        .catch(() => {});
    }
  }, [section]);

  const handleDeleteDomain = async (id) => {
    try {
      await deleteCategory(id);
      setCategories((prev) => prev.filter((x) => x.id !== id));
      toast("Domain deleted", "info");
    } catch (e) {
      toast(e.response?.data?.message || "Error", "error");
    }
  };

  return (
    <div className="admin-layout">
      {ConfirmComponent}
      <AdminSidebar section={section} onSectionChange={setSection} />
      <div className="admin-content">
        {section === "dashboard" && <DashboardSection stats={stats} />}
        {section === "courses" && (
          <CoursesSection providers={providers} categories={categories} />
        )}
        {section === "partners" && (
          <PartnersSection
            providers={providers}
            onRefresh={() =>
              fetchProviders().then((r) => setProviders(r.data.data))
            }
          />
        )}
        {section === "domains" && (
          <DomainsSection
            categories={categories}
            onDelete={handleDeleteDomain}
            onRefresh={() =>
              fetchCategories().then((r) => setCategories(r.data.data))
            }
          />
        )}
        {section === "users" && <UsersSection courses={[]} />}
        {section === "registered_users" && <RegisteredUsersSection />}
        {section === "course_enrollments" && <CourseEnrollmentsSection />}
        {section === "jobs_admin" && <JobsSection />}
        {section === "applications" && <ApplicationsSection />}
        {section === "counsel_admin" && <CounselSection />}
      </div>
    </div>
  );
};

// ─── Root AdminPage ───────────────────────────────────────────────────────────
const AdminPage = () => {
  // Auth is handled by AdminRoute in App.js — if we reach here, user is admin
  return (
    <ToastProvider>
      <Routes>
        <Route path="/" element={<AdminDashboard />} />
        <Route path="/jobs" element={<AdminDashboard />} />
        <Route path="/post-job" element={<PostJobPage />} />
        <Route path="/add-partner" element={<AddPartnerPage />} />
        <Route path="/add-enrollment" element={<AddEnrollmentPage />} />
        <Route path="/edit-enrollment/:id" element={<EditEnrollmentPage />} />
        <Route path="/testimonials" element={<TestimonialsPage />} />
        <Route path="/add-testimonial" element={<AddTestimonialPage />} />
        <Route path="/edit-testimonial/:id" element={<EditTestimonialPage />} />
        <Route path="/blogs" element={<BlogsAdminPage />} />
        <Route path="/add-blog" element={<AddBlogPage />} />
        <Route path="/edit-blog/:id" element={<EditBlogPage />} />
      </Routes>
    </ToastProvider>
  );
};

export default AdminPage;
